import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

interface User {
  _id: string;
  email: string;
  name: string;
  level: number;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  billingEmail?: string;
  phoneNumber?: string;
}

interface AuthContextType {
  token: string | null;
  setToken: (token: string | null) => void;
  isAuthenticated: boolean;
  currentUser: User | null;
  isAuthorized: boolean;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch current user when token changes
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!token) {
        setIsLoading(false);
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setCurrentUser(null);
        return;
      }
      
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const userData = await response.json();
        setCurrentUser(userData);
        setIsAuthenticated(true);
        
        // Check if user has admin privileges (level 2 which is displayed as level 3)
        setIsAuthorized(userData.level === 2);
        
        setError(null);
      } catch (err) {
        console.error('Failed to fetch current user:', err);
        setError('Authentication failed. Please try again.');
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCurrentUser();
  }, [token]);

  useEffect(() => {
    // Listen for messages from parent window
    const handleMessage = (event: MessageEvent) => {
      // In production, verify the origin
      // if (event.origin !== 'https://your-parent-domain.com') return;
      
      if (event.data && event.data.type === 'SET_TOKEN') {
        setToken(event.data.token);
    
        // Send confirmation back to parent
        if (event.source && 'postMessage' in event.source) {
          (event.source as Window).postMessage({
            type: 'TOKEN_RECEIVED'
          }, event.origin);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Check URL params for token (alternative method)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      setToken(urlToken);
    }
    
    // Notify parent that admin panel is ready to receive token
    if (window.self !== window.top) {
      window.parent.postMessage({ type: 'ADMIN_READY' }, '*');
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ 
      token, 
      setToken, 
      isAuthenticated, 
      currentUser, 
      isAuthorized,
      isLoading,
      error
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; 